import { RatingsTask } from 'Tasks/RatingsTask/RatingsTask'
import { IContainerStrategy } from '../ContainerStrategy'
import {
    AttributeDefinitionType,
    AttributesData,
    ContainerDataType,
    ContainerType,
    QCAttributesData,
    RatingsAttributesData,
    RatingsOnLinearConversationDataType,
    RoleType,
    SourceType,
    TaskConfigType,
    TaskMode,
} from 'Types/Tasks.types'
import { cloneDeep } from 'lodash'
import { isViewerMode } from 'Utils/task.utils'

export class RatingsContainerStrategy
    implements
        IContainerStrategy<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >
{
    containerContext?: {
        task: RatingsTask
    }

    setContext(containerContext: { task: RatingsTask }) {
        this.containerContext = containerContext
    }

    getEmptyDraftContainer(): ContainerDataType<
        ContainerType.RatingsOnLinearConversation,
        RatingsOnLinearConversationDataType
    > {
        return {
            data: {
                schema_version: 1,
                type: ContainerType.RatingsOnLinearConversation,
                data: {
                    version_id: null,
                    role: RoleType.ASSISTANT,
                    source: SourceType.HUMAN,
                    extra_info: {},
                    responses_ratings: [],
                    quality_check: {
                        evaluation_on_version_id: {},
                    },
                },
            },
        }
    }

    getContainerFromOutputStart(
        outputStartContainer: ContainerDataType<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >,
        mode: TaskMode
    ) {
        const draftContainer = cloneDeep(outputStartContainer)
        if (isViewerMode(mode)) return draftContainer

        draftContainer!.data.data.quality_check.evaluation_on_version_id = {}
        draftContainer!.decision = null
        return draftContainer!
    }

    getModifiedDraftOutputContainer = (
        draftOutputContainer: ContainerDataType<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >,
        taskConfig: TaskConfigType<
            ContainerType.RatingsOnLinearConversation,
            RatingsOnLinearConversationDataType
        >
    ) => {
        const draftContainer = cloneDeep(draftOutputContainer)

        const extraInfo: AttributesData = {}
        const reqdQCAttributes: { [versionId: string]: QCAttributesData } = {}
        const reqdResponsesRatings: RatingsAttributesData[] = []

        if (!draftContainer) {
            return draftContainer
        }

        const filteredAttributeNames = new Set(
            taskConfig.assignment.task.attributes
                .filter(attr => attr.type === AttributeDefinitionType.EXTRA_INFO)
                .map(attr => attr.name)
        )
        const filteredQCAttributeNames = new Set(
            taskConfig.assignment.task.attributes
                .filter(attr => attr.type === AttributeDefinitionType.QUALITY_EVALUATION)
                .map(attr => attr.name)
        )
        const filteredResponseRatingsAttributeNames = new Set(
            taskConfig.assignment.task.attributes
                .filter(attr => attr.type === AttributeDefinitionType.RESPONSE_RATING)
                .map(attr => attr.name)
        )

        if (draftContainer.data.data.extra_info) {
            for (const [attributeName, attributeValue] of Object.entries(
                draftContainer.data.data.extra_info
            )) {
                if (filteredAttributeNames.has(attributeName)) {
                    extraInfo[attributeName] = attributeValue
                }
            }
        }
        if (draftContainer.data.data.quality_check.evaluation_on_version_id) {
            for (const [versionId, qcAttributes] of Object.entries(
                draftContainer.data.data.quality_check.evaluation_on_version_id
            )) {
                const reqdQCAttributesData: QCAttributesData = {}
                for (const [attributeName, attributeData] of Object.entries(qcAttributes)) {
                    if (filteredQCAttributeNames.has(attributeName)) {
                        reqdQCAttributesData[attributeName] = attributeData
                    }
                }
                reqdQCAttributes[versionId] = reqdQCAttributesData
            }
        }
        if (draftContainer.data.data.responses_ratings) {
            for (const responseRatings of draftContainer.data.data.responses_ratings) {
                const reqdRatings: AttributesData = {}
                for (const [attributeName, attributeValue] of Object.entries(
                    responseRatings.ratings
                )) {
                    if (filteredResponseRatingsAttributeNames.has(attributeName)) {
                        reqdRatings[attributeName] = attributeValue
                    }
                }
                reqdResponsesRatings.push({
                    ratings: reqdRatings,
                    response_id: responseRatings.response_id,
                })
            }
        }
        draftContainer.data.data.extra_info = extraInfo
        draftContainer.data.data.quality_check.evaluation_on_version_id = reqdQCAttributes
        draftContainer.data.data.responses_ratings = reqdResponsesRatings
        return draftContainer
    }
}
